﻿import { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Spinner as BsSpinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useReport } from '@/hooks/useReport';
import { useToast } from '@/components/common/Toast';
import type { Visit } from '@/hooks/useVisits';

interface Props {
  visit: Visit | null;
  show: boolean;
  onHide: () => void;
}

export default function EmailModal({ visit, show, onHide }: Props) {
  const { t } = useTranslation('visits');
  const { t: tc } = useTranslation();
  const { preview, sendEmail, previewing, sending } = useReport();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (show && visit) {
      setEmail(visit.emailinvioreport || '');
      loadPreview();
    } else {
      setPdfUrl(null);
      setEmail('');
    }
  }, [show, visit]);

  const loadPreview = async () => {
    if (!visit) return;
    const blob = await preview(visit.id, visit.versione, visit.ragsociale || '');
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
    }
  };

  const handleSend = async () => {
    if (!visit || !email.trim()) {
      showToast('Attenzione', 'Inserisci almeno un destinatario', 'warning');
      return;
    }
    const ok = await sendEmail(visit.id, visit.versione, visit.ragsociale || '', email);
    if (ok) {
      showToast('Successo', 'Email inviata con successo', 'success');
      onHide();
    } else {
      showToast('Errore', 'Errore nell\'invio dell\'email', 'danger');
    }
  };

  const handleClose = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('sendEmail')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* PDF Preview */}
        <div style={{ width: '100%', height: 500, border: '1px solid #dee2e6', borderRadius: 4, marginBottom: 16, overflow: 'hidden' }}>
          {previewing && (
            <div className="d-flex justify-content-center align-items-center h-100">
              <BsSpinner animation="border" className="spinner-primary" />
            </div>
          )}
          {!previewing && pdfUrl && (
            <iframe ref={iframeRef} src={pdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="PDF Preview" />
          )}
          {!previewing && !pdfUrl && (
            <div className="d-flex justify-content-center align-items-center h-100 text-muted">
              Errore nel caricamento del preview
            </div>
          )}
        </div>

        {/* Email recipients */}
        <Form.Group>
          <Form.Label>{t('emailRecipients')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="email1@example.com, email2@example.com"
          />
          <Form.Text className="text-muted">
            Separare più destinatari con una virgola
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>{tc('actions.close')}</Button>
        <Button variant="danger" onClick={handleSend} disabled={sending || !email.trim()}>
          {sending ? <BsSpinner animation="border" size="sm" className="me-1" /> : null}
          {t('sendEmail')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
